// AI Recording Studio Pro — AI Assistant
// Contextual AI assistant for script generation, course planning, and DevOps guidance
import { useState, useRef, useEffect, useCallback } from "react";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Send, Copy, Download, RefreshCw, Sparkles, BookOpen,
  FileText, ListChecks, Code, Terminal, HelpCircle, MessageSquare,
  Lightbulb, Target, Clock, Wand2, GraduationCap, AlertTriangle,
  ChevronRight, Check, Loader2, Save, History, Star, Trash2,
  Plus, Zap,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const AI_TEMPLATES = [
  { id: "script", label: "Script vidéo", icon: FileText, color: "#6366f1",
    description: "Générer un script de formation complet",
    prompt: "Génère un script de vidéo de formation sur le sujet suivant. Inclus une introduction engageante, les points clés à aborder, les commandes/exemples à montrer, et une conclusion avec récapitulatif." },
  { id: "plan", label: "Plan de cours", icon: ListChecks, color: "#22c55e",
    description: "Créer un plan de cours structuré",
    prompt: "Crée un plan de cours détaillé avec objectifs pédagogiques, prérequis, chapitres, durée estimée, exercices pratiques et évaluation." },
  { id: "intro", label: "Intro / Outro", icon: Sparkles, color: "#f97316",
    description: "Rédiger une intro et outro professionnelles",
    prompt: "Rédige une introduction et une conclusion professionnelles pour une vidéo de formation. L'intro doit accrocher l'attention, présenter le sujet et les objectifs. L'outro doit récapituler, donner des ressources complémentaires et encourager la pratique." },
  { id: "commands", label: "Commandes", icon: Terminal, color: "#3b82f6",
    description: "Générer des commandes contextuelles",
    prompt: "Liste les commandes essentielles avec explications détaillées, exemples pratiques et pièges courants à éviter." },
  { id: "quiz", label: "Quiz / Exercices", icon: GraduationCap, color: "#8b5cf6",
    description: "Créer des questions et exercices",
    prompt: "Crée 10 questions de quiz (QCM) avec explications pour chaque réponse, plus 3 exercices pratiques avec solution détaillée." },
  { id: "errors", label: "Erreurs courantes", icon: AlertTriangle, color: "#ef4444",
    description: "Lister les erreurs fréquentes",
    prompt: "Liste les erreurs les plus courantes que font les débutants, avec les messages d'erreur typiques, leurs causes et comment les résoudre." },
  { id: "bestpractices", label: "Bonnes pratiques", icon: Target, color: "#06b6d4",
    description: "Documenter les best practices",
    prompt: "Liste les bonnes pratiques essentielles avec exemples de ce qu'il faut faire et ne pas faire, et pourquoi." },
  { id: "summary", label: "Résumé", icon: BookOpen, color: "#ec4899",
    description: "Résumer un sujet technique",
    prompt: "Fais un résumé clair et concis adapté pour une vidéo de formation. Inclus les concepts clés, un schéma mental, et les points à retenir." },
];

const TECH_CONTEXTS = [
  "Linux", "Docker", "Kubernetes", "Terraform", "Ansible", "Jenkins", "GitLab CI",
  "GitHub Actions", "AWS", "Azure", "GCP", "Prometheus", "Grafana", "ELK Stack",
  "Nginx", "HAProxy", "PostgreSQL", "MongoDB", "Redis", "Git", "Python", "Bash",
  "Wireshark", "Nmap", "Metasploit", "OWASP", "Réseau", "DNS", "DHCP", "Firewall",
];

export default function RecordingAIAssistant() {
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [topic, setTopic] = useState("");
  const [techContext, setTechContext] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [activeTab, setActiveTab] = useState("templates");

  const resultRef = useRef(null);
  const chatEndRef = useRef(null);
  const token = localStorage.getItem("token");

  // Scroll to result
  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  // Scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Generate with AI
  const generateContent = async () => {
    if (!topic.trim()) return toast.error("Entrez un sujet");
    if (!activeTemplate) return toast.error("Sélectionnez un template");

    setLoading(true);
    setResult("");

    const fullPrompt = `${activeTemplate.prompt}\n\nSujet : ${topic}${techContext ? `\nContexte technique : ${techContext}` : ""}${additionalContext ? `\nInstructions supplémentaires : ${additionalContext}` : ""}`;

    try {
      const res = await fetch(`${API}/api/studio/ai/generate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: fullPrompt,
          template_type: activeTemplate.id,
          topic,
          tech_context: techContext,
        }),
      });

      if (!res.ok) {
        // Fallback: generate locally
        const generated = generateLocalFallback(activeTemplate, topic, techContext);
        setResult(generated);
        addToHistory(activeTemplate, topic, generated);
        return;
      }

      const data = await res.json();
      setResult(data.content || data.result || "");
      addToHistory(activeTemplate, topic, data.content || data.result || "");
    } catch {
      // Fallback
      const generated = generateLocalFallback(activeTemplate, topic, techContext);
      setResult(generated);
      addToHistory(activeTemplate, topic, generated);
    } finally {
      setLoading(false);
    }
  };

  // Local fallback generation
  const generateLocalFallback = (template, topic, tech) => {
    const techStr = tech || "DevOps";
    switch (template.id) {
      case "script":
        return `# Script vidéo : ${topic}\n\n## Introduction (2 min)\n\n"Bonjour à tous ! Aujourd'hui, nous allons explorer **${topic}** en profondeur.\nDans cette vidéo, vous allez apprendre :\n- Les concepts fondamentaux\n- La mise en pratique étape par étape\n- Les bonnes pratiques et pièges à éviter\n\nC'est parti !"\n\n## Partie 1 : Concepts (5 min)\n\n- Définition de ${topic}\n- Pourquoi c'est important en ${techStr}\n- Architecture et fonctionnement\n- Comparaison avec les alternatives\n\n## Partie 2 : Démonstration pratique (10 min)\n\n### Prérequis\n\`\`\`bash\n# Vérifier les prérequis\necho "Prérequis pour ${topic}"\n\`\`\`\n\n### Étape 1 : Installation\n\`\`\`bash\n# Installation de ${topic}\necho "Installation en cours..."\n\`\`\`\n\n### Étape 2 : Configuration\n\`\`\`bash\n# Configuration de base\necho "Configuration terminée"\n\`\`\`\n\n### Étape 3 : Vérification\n\`\`\`bash\n# Vérification\necho "Tout fonctionne !"\n\`\`\`\n\n## Partie 3 : Bonnes pratiques (3 min)\n\n✅ **À faire :**\n- Point 1\n- Point 2\n- Point 3\n\n❌ **À éviter :**\n- Erreur courante 1\n- Erreur courante 2\n\n## Conclusion (2 min)\n\n"Récapitulons ce que nous avons vu aujourd'hui :\n1. Les bases de ${topic}\n2. La mise en pratique\n3. Les bonnes pratiques\n\nPour aller plus loin, je vous recommande la documentation officielle.\nN'hésitez pas à poser vos questions en commentaire.\nÀ la prochaine !"`;
      case "plan":
        return `# Plan de cours : ${topic}\n\n## Informations\n- **Durée** : 4 heures\n- **Niveau** : Intermédiaire\n- **Prérequis** : Bases de ${techStr}\n\n## Objectifs pédagogiques\n1. Comprendre les concepts fondamentaux de ${topic}\n2. Savoir mettre en place ${topic} en environnement professionnel\n3. Appliquer les bonnes pratiques\n4. Résoudre les problèmes courants\n\n## Chapitres\n\n### Chapitre 1 : Introduction (30 min)\n- Contexte et enjeux\n- Vue d'ensemble\n- Architecture\n\n### Chapitre 2 : Installation et configuration (45 min)\n- Prérequis système\n- Installation\n- Configuration de base\n- Exercice pratique\n\n### Chapitre 3 : Utilisation avancée (60 min)\n- Fonctionnalités avancées\n- Cas d'usage réels\n- Lab pratique\n\n### Chapitre 4 : Bonnes pratiques et sécurité (30 min)\n- Best practices\n- Sécurisation\n- Monitoring\n\n### Chapitre 5 : Troubleshooting (30 min)\n- Erreurs courantes\n- Diagnostic\n- Résolution\n\n### Évaluation (45 min)\n- Quiz de 20 questions\n- Exercice pratique noté\n- Projet final\n\n## Ressources\n- Documentation officielle\n- Labs pratiques\n- Aide-mémoire PDF`;
      default:
        return `# ${template.label} : ${topic}\n\nContenu généré pour "${topic}" dans le contexte ${techStr}.\n\n> **Note** : Connectez le module IA (OpenAI) pour une génération plus riche.\n> Configurez la clé API dans Administration > Config IA.\n\n## Points clés\n\n1. Introduction à ${topic}\n2. Concepts fondamentaux\n3. Mise en pratique\n4. Bonnes pratiques\n5. Exercices\n\n## Commandes essentielles\n\n\`\`\`bash\n# Commandes pour ${topic}\necho "${topic} - démarrage"\n\`\`\``;
    }
  };

  // Add to history
  const addToHistory = (template, topic, content) => {
    setHistory((prev) => [{
      id: Date.now(),
      template: template.label,
      topic,
      content,
      timestamp: new Date().toISOString(),
    }, ...prev].slice(0, 50));
  };

  // Copy result
  const copyResult = () => {
    navigator.clipboard.writeText(result).then(() => toast.success("Copié dans le presse-papier"));
  };

  // Download as markdown
  const downloadResult = () => {
    const blob = new Blob([result], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTemplate?.id || "ai"}_${topic.replace(/\s+/g, "_").slice(0, 30)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Fichier téléchargé");
  };

  // Chat send
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    const userMsg = { role: "user", content: chatInput, timestamp: new Date().toISOString() };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");

    // Simulate AI response
    const aiResponse = `Voici ma réponse concernant "${chatInput}".\n\nPour une réponse plus détaillée, connectez le module IA (OpenAI) dans la configuration de la plateforme.`;
    setTimeout(() => {
      setChatMessages((prev) => [...prev, { role: "assistant", content: aiResponse, timestamp: new Date().toISOString() }]);
    }, 800);
  };

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #3b82f6, #06b6d4)", boxShadow: "0 4px 12px rgba(59,130,246,0.3)" }}>
              <Brain className="h-5 w-5 text-white" />
            </div>
            AI Assistant
          </h1>
          <p className="text-sm text-muted-foreground mt-1 ml-[52px]">
            Générez des scripts, plans de cours et contenus pédagogiques avec l'IA
          </p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="templates" className="gap-1.5"><Wand2 className="h-3.5 w-3.5" /> Templates</TabsTrigger>
            <TabsTrigger value="chat" className="gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Chat IA</TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5"><History className="h-3.5 w-3.5" /> Historique</TabsTrigger>
          </TabsList>

          {/* ── Templates Tab ── */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Template selection + config */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" /> Choisir un template
                </h3>
                <div className="space-y-2">
                  {AI_TEMPLATES.map((tmpl, i) => {
                    const Icon = tmpl.icon;
                    const isActive = activeTemplate?.id === tmpl.id;
                    return (
                      <motion.button key={tmpl.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }} onClick={() => setActiveTemplate(tmpl)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          isActive ? "border-primary bg-primary/5 shadow-sm" : "border-border/50 hover:border-primary/30 hover:bg-muted/30"
                        }`}>
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: `${tmpl.color}15`, color: tmpl.color }}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold truncate">{tmpl.label}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{tmpl.description}</p>
                        </div>
                        {isActive && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Center: Input form */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" /> Configuration
                </h3>
                <Card>
                  <CardContent className="pt-4 pb-4 space-y-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Sujet *</label>
                      <Input value={topic} onChange={(e) => setTopic(e.target.value)}
                        placeholder="Ex: Déployer un cluster K8s avec kubeadm" className="text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Contexte technique</label>
                      <Select value={techContext} onValueChange={setTechContext}>
                        <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                        <SelectContent>
                          {TECH_CONTEXTS.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Instructions supplémentaires</label>
                      <Textarea value={additionalContext} onChange={(e) => setAdditionalContext(e.target.value)}
                        placeholder="Ex: Niveau débutant, inclure des diagrammes ASCII..." rows={3} className="text-xs" />
                    </div>
                    <Button onClick={generateContent} disabled={loading || !topic.trim() || !activeTemplate}
                      className="w-full gap-2" style={{ background: "linear-gradient(135deg, #3b82f6, #06b6d4)", border: "none" }}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                      {loading ? "Génération en cours..." : "Générer"}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Right: Result */}
              <div className="space-y-4" ref={resultRef}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" /> Résultat
                  </h3>
                  {result && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={copyResult} title="Copier">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={downloadResult} title="Télécharger">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <Card className="min-h-[400px]">
                  <CardContent className="pt-4 pb-4">
                    {loading ? (
                      <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                        <p className="text-sm text-muted-foreground">Génération en cours...</p>
                      </div>
                    ) : result ? (
                      <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{result}</pre>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 text-center">
                        <Brain className="h-12 w-12 text-muted-foreground/20 mb-3" />
                        <p className="text-sm text-muted-foreground">Sélectionnez un template et un sujet</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Le résultat apparaîtra ici</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ── Chat Tab ── */}
          <TabsContent value="chat">
            <Card className="max-w-3xl mx-auto">
              <CardContent className="p-0">
                <div className="h-[60vh] flex flex-col">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {chatMessages.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <Brain className="h-16 w-16 text-muted-foreground/10 mb-4" />
                        <h3 className="font-semibold text-lg">AI Assistant Chat</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">
                          Posez des questions techniques sur Linux, Docker, Kubernetes, Cloud,
                          Réseau, Cybersécurité... L'IA vous aidera dans votre préparation.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-6 justify-center">
                          {["Comment déployer un pod K8s ?", "Explique les namespaces Docker", "Commandes iptables essentielles"].map((q) => (
                            <Button key={q} variant="outline" size="sm" className="text-xs"
                              onClick={() => { setChatInput(q); }}>
                              <Lightbulb className="h-3 w-3 mr-1" /> {q}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    {chatMessages.map((msg, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          msg.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        }`}>
                          <pre className="text-sm whitespace-pre-wrap font-sans">{msg.content}</pre>
                        </div>
                      </motion.div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="border-t border-border/50 p-3 flex gap-2">
                    <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendChatMessage()}
                      placeholder="Posez une question technique..." className="text-sm" />
                    <Button onClick={sendChatMessage} disabled={!chatInput.trim()} size="icon"
                      style={{ background: "linear-gradient(135deg, #3b82f6, #06b6d4)", border: "none" }}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── History Tab ── */}
          <TabsContent value="history">
            <div className="max-w-3xl mx-auto space-y-3">
              {history.length > 0 ? history.map((item) => (
                <Card key={item.id} className="hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => { setResult(item.content); setActiveTab("templates"); }}>
                  <CardContent className="pt-3 pb-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{item.template}</Badge>
                          <h4 className="text-sm font-medium truncate">{item.topic}</h4>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(item.timestamp).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(item.content);
                          toast.success("Copié");
                        }}><Copy className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => {
                          e.stopPropagation();
                          setHistory((prev) => prev.filter((h) => h.id !== item.id));
                        }}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <div className="text-center py-16">
                  <History className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Aucun historique</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </RecordingStudioLayout>
  );
}
